import {MoreVertical} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import Popover from 'src/Layer/Popover';
import {cn} from 'src/lib/utils';
import {PopoverMenuContext} from 'src/PopoverMenuContext';
import type {PopoverMenuContextValue} from 'src/PopoverMenuContext';

type Props = {
  children: React.ReactNode;
  /** Replaces the default ⋮ icon button. */
  renderTrigger?: (props: {
    ref: React.RefCallback<HTMLButtonElement>;
    onClick: () => void;
  }) => React.ReactNode;
  /** Opens upward, for a trigger pinned to the bottom of the screen. */
  above?: boolean;
};

export const PopoverMenu = ({children, renderTrigger, above = false}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const closeMenu = React.useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = React.useCallback(
    () => setIsMenuOpen((state) => !state),
    [],
  );

  const contextValue = React.useMemo<PopoverMenuContextValue>(
    () => ({
      closeMenu,
    }),
    [closeMenu],
  );

  const content = (
    <PopoverMenuContext.Provider value={contextValue}>
      {/* Capped below the visual viewport so Popover's clamp logic (which
          only engages when the whole box fits on screen) can always pull the
          menu fully into view, and the list scrolls internally instead of
          running off the bottom on short viewports. The rem bound keeps the
          cap from ever clipping the tallest menu on desktop. */}
      <div
        className={cn(
          'shadow-popover bg-popover text-popover-foreground outline-ring/50',
          'flex max-h-[min(70dvh,40rem)] flex-col overflow-y-auto rounded-md outline-1',
        )}
      >
        {children}
      </div>
    </PopoverMenuContext.Provider>
  );

  return (
    <Popover
      open={isMenuOpen}
      popoverContents={content}
      popoverAnchor={above ? 'above-right' : 'top-right'}
      onOutsideClick={closeMenu}
    >
      {(anchorRef) =>
        renderTrigger ? (
          renderTrigger({ref: anchorRef, onClick: toggleMenu})
        ) : (
        <Button
          variant="ghost"
          size="icon"
          ref={anchorRef}
          onClick={toggleMenu}
        >
          <MoreVertical className="size-5" />
        </Button>
        )
      }
    </Popover>
  );
};
